import axios from "axios";
import moment from "dayjs";
import { auth } from "./Authentication";

const axiosIns = axios.create({
  baseURL: import.meta.env.VITE_SERVICE_URL,
  headers: {},
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

const getStoredUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem("current_user"));
  } catch (_) {
    return auth.user;
  }
};

// Decode JWT exp without verification (client-side only, for expiry check)
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // convert to ms
  } catch (_) {
    return 0;
  }
};

// Returns true if token is expired or within 60s of expiry
const isTokenExpiredOrExpiring = (token) => {
  if (!token) return true;
  return Date.now() >= getTokenExpiry(token) - 60000;
};

// Returns a Promise that resolves with the new token
const authRefreshLogic = () => {
  const currentUser = getStoredUser();
  if (!currentUser || !currentUser.token) {
    return Promise.reject(new Error("No token"));
  }

  return axios
    .post(`${import.meta.env.VITE_SERVICE_URL}/user/refresh-token`, {
      token: currentUser.token,
    })
    .then((res) => {
      const newToken = res.data.token;
      const user = { ...currentUser, lastTokenRefresh: new Date(), token: newToken };
      auth.set_user(user);
      return newToken;
    })
    .catch((err) => {
      // Refresh failed for any reason — kick user out, no zombie session
      auth.remove_user();
      window.location.href = "/login";
      return Promise.reject(err);
    });
};

axiosIns.interceptors.request.use(
  function (config) {
    if (config.url && /^https?:\/\//i.test(config.url)) {
      config.url = config.url.replace(/^https?:\/\/[^/]+/i, import.meta.env.VITE_SERVICE_URL);
    }

    if (auth.is_loggedin() && !auth.has_logged_out()) {
      const currentUser = getStoredUser();
      if (!currentUser) return config;

      if (isTokenExpiredOrExpiring(currentUser.token)) {
        // Token is expired or about to expire — must refresh before sending this request
        if (isRefreshing) {
          // Another refresh is already in progress — queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token) => {
                config.headers["Authorization"] = `Bearer ${token}`;
                resolve(config);
              },
              reject,
            });
          });
        }

        isRefreshing = true;
        return new Promise((resolve, reject) => {
          authRefreshLogic()
            .then((newToken) => {
              config.headers["Authorization"] = `Bearer ${newToken}`;
              processQueue(null, newToken);
              resolve(config);
            })
            .catch((err) => {
              processQueue(err, null);
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      // Token is valid — send request immediately
      config.headers["Authorization"] = `Bearer ${currentUser.token}`;

      // Proactive refresh if > 15 min since last refresh (fire-and-forget, guarded)
      const minutesSinceRefresh = moment().diff(
        moment(currentUser.lastTokenRefresh || new Date()),
        "minutes"
      );
      if (minutesSinceRefresh > 15 && !isRefreshing) {
        isRefreshing = true;
        authRefreshLogic()
          .catch(() => {}) // already handled inside authRefreshLogic
          .finally(() => {
            isRefreshing = false;
          });
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosIns.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response && error.response.status === 401) {
      if (error.response.config.url === "user/auth") {
        auth.remove_user();
      } else {
        auth.remove_user();
        window.localStorage.setItem("next", window.location.pathname);
        window.location.href = `/login?next=${window.location.pathname}`;
      }
    }
    return Promise.reject(error);
  }
);

const touchlessAxios = axios.create({
  baseURL: import.meta.env.VITE_SERVICE_URL,
});

touchlessAxios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    return Promise.reject(error);
  }
);

window.touchlessAxiosIns = touchlessAxios;
window.axiosIns = axiosIns;
export { axiosIns, touchlessAxios };
